/**
 * Pure derivations for the v2 app screen (Dashboard).
 *
 * Everything here is a plain function over data the API already returns —
 * no fetching, no formatting side effects — so the screen stays a layout and
 * the arithmetic stays testable.
 */
import type {
  GoalType,
  Projection,
  UserProfileAI,
  WeeklyReviewPerDay,
  WeightLog,
} from '@onflow/shared';

import type { ConsistencyDay, ConsistencyTone } from '../components/v2/ConsistencyStrip.js';
import type { TrackState } from '../components/v2/FlowStatus.js';
import type { ProjectionPoint } from '../components/v2/ProjectionChart.js';
import type { ScoreMark, ScorecardDay } from '../components/v2/Scorecard.js';

const MS_PER_DAY = 86_400_000;

/** Time-of-day greeting. Boundaries at 12:00 and 17:00 local. */
export function greeting(now: Date): string {
  const h = now.getHours();
  if (h < 12) return 'Morning';
  if (h < 17) return 'Afternoon';
  return 'Evening';
}

/** "YYYY-MM-DD" in the browser's local timezone (not UTC). */
export function localDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * The locked goal as one line: "Gain 3.0 kg of muscle". Falls back to a
 * prompt when the profile has no goal yet — the strip is still worth showing
 * because it is where the user goes to set one.
 */
export function goalHeadline(profile: UserProfileAI | null | undefined): string {
  if (!profile?.goalType) return 'No goal set yet';
  const from = profile.currentWeightKg;
  const to = profile.targetWeightKg;
  const delta = from !== null && to !== null ? Math.abs(to - from) : null;
  const kg = delta === null ? null : `${delta.toFixed(1)} kg`;
  const labels: Record<GoalType, string> = {
    lose_weight: kg ? `Lose ${kg}` : 'Lose weight',
    gain_muscle: kg ? `Gain ${kg} of muscle` : 'Gain muscle',
    bulk: kg ? `Gain ${kg}` : 'Bulk up',
    maintain: to !== null ? `Hold ${to.toFixed(1)} kg` : 'Maintain weight',
    other: 'Your goal',
  };
  return labels[profile.goalType];
}

/** "Jul 1 → Sep 12". Start falls back to the profile creation date. */
export function deadlineRange(startIso: string | null, targetIso: string | null): string {
  if (!targetIso) return 'No target date';
  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return startIso ? `${fmt(startIso)} → ${fmt(targetIso)}` : fmt(targetIso);
}

/** Whole days from now until the target date. Never negative. */
export function daysLeft(targetIso: string | null, now: Date): number | undefined {
  if (!targetIso) return undefined;
  const diff = new Date(targetIso).getTime() - now.getTime();
  return Math.max(0, Math.ceil(diff / MS_PER_DAY));
}

/**
 * How far the user has moved toward the target weight, 0-100. Undefined when
 * the goal has no weight delta to measure against (maintain, or a missing
 * start/target), which hides the strip's progress hairline rather than
 * drawing a meaningless full bar.
 */
export function goalProgressPct(
  startKg: number | null | undefined,
  targetKg: number | null | undefined,
  latestKg: number | null | undefined,
): number | undefined {
  if (startKg == null || targetKg == null) return undefined;
  const span = targetKg - startKg;
  if (Math.abs(span) < 0.05) return undefined;
  const moved = (latestKg ?? startKg) - startKg;
  return Math.max(0, Math.min(100, (moved / span) * 100));
}

/** Percent of the local day that has elapsed, 0-100. */
export function dayElapsedPct(now: Date): number {
  return ((now.getHours() * 60 + now.getMinutes()) / 1440) * 100;
}

/**
 * Maps the server's projection union onto the v2 status triad. `behind` and
 * `flat` are amber rather than red — they mean the date is slipping, not that
 * the user is moving the wrong way.
 */
export function projectionState(projection: Projection | undefined): TrackState {
  switch (projection?.kind) {
    case 'on-track':
      return 'on-track';
    case 'behind':
    case 'flat':
      return 'drifting';
    case 'wrong-direction':
    case 'goal-passed':
      return 'off-track';
    default:
      return 'neutral';
  }
}

/**
 * Turns the weekly review's per-day adherence rows into strip bars.
 *
 * tone: `none` when nothing was logged, `hit` when the day met both
 * tolerances, `over` above the +15% kcal band (ADR-0012/0017), `under`
 * otherwise. Bar height is percent of the day's kcal target.
 */
export function consistencyDays(
  perDay: WeeklyReviewPerDay[] | undefined,
  today: string,
): ConsistencyDay[] {
  if (!perDay) return [];
  return perDay.map((d) => {
    const pct = d.targetKcal > 0 ? (d.actualKcal / d.targetKcal) * 100 : 0;
    let tone: ConsistencyTone;
    if (d.actualKcal === 0) tone = 'none';
    else if (d.dayMet) tone = 'hit';
    else if (pct > 115) tone = 'over';
    else tone = 'under';
    return {
      label: String(Number(d.date.slice(8, 10))),
      value: pct,
      tone,
      detail: `${Math.round(d.actualKcal)} of ${Math.round(d.targetKcal)} kcal`,
      today: d.date === today,
    };
  });
}

/**
 * The one-line read on the strip. Mirrors the product claim: if adherence is
 * off, say where the gap came from rather than cheerleading.
 */
export function consistencySentence(days: ConsistencyDay[]): string {
  if (days.length === 0) return 'No logs yet this week. Log a day and this fills in.';
  const logged = days.filter((d) => d.tone !== 'none').length;
  if (logged === 0) return 'Nothing logged this week. The strip fills in as you log.';
  const hit = days.filter((d) => d.tone === 'hit').length;
  const over = days.filter((d) => d.tone === 'over').length;
  const under = days.filter((d) => d.tone === 'under').length;
  if (hit === logged) return `${hit} of ${logged} logged days in range. That is the whole job.`;
  if (over >= under)
    return `${over} day${over === 1 ? '' : 's'} over target out of ${logged} logged. That is where a gap comes from, not the training.`;
  return `${under} day${under === 1 ? '' : 's'} under target out of ${logged} logged. Under-eating stalls progress as surely as over-eating.`;
}

// ── Train screen ─────────────────────────────────────────────────────────

export interface WeekScorecard {
  days: ScorecardDay[];
  marks: ScoreMark[];
  sentence: string;
}

/**
 * This week's training at a glance, Monday-first.
 *
 * A day with a saved workout is a hit; a past day without one is a miss; a
 * future day is neutral (`rest`) because it hasn't had the chance to fail
 * yet. Anything the API returns outside the current week is ignored.
 */
export function weekScorecard(workouts: ReadonlyArray<{ date: string }>, now: Date): WeekScorecard {
  const labels = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  // getDay() is Sunday-first; shift so Monday is index 0.
  const todayIdx = (now.getDay() + 6) % 7;
  const monday = new Date(now);
  monday.setHours(0, 0, 0, 0);
  monday.setDate(monday.getDate() - todayIdx);

  const trained = new Set(
    workouts
      .map((w) => new Date(w.date))
      .filter((d) => {
        const offset = Math.floor((d.getTime() - monday.getTime()) / MS_PER_DAY);
        return offset >= 0 && offset < 7;
      })
      .map((d) => (d.getDay() + 6) % 7),
  );

  const marks: ScoreMark[] = labels.map((_, i) => {
    if (trained.has(i)) return 'hit';
    return i > todayIdx ? 'rest' : 'miss';
  });

  const sessions = trained.size;
  const sentence =
    sessions === 0
      ? 'No sessions logged this week yet.'
      : `${sessions} session${sessions === 1 ? '' : 's'} logged this week.`;

  return {
    days: labels.map((label, i) => ({ label, today: i === todayIdx })),
    marks,
    sentence,
  };
}

// ── Review screen ────────────────────────────────────────────────────────

export interface ProjectionSeries {
  points: ProjectionPoint[];
  required: ProjectionPoint[];
  goalValue: number | undefined;
}

/**
 * The two lines behind the projection chart.
 *
 * `points` is the user's actual weight history (oldest first, capped so the
 * chart stays readable). `required` is the straight line from the starting
 * weight on day 0 to the target weight on the target date, sampled at the
 * same number of steps — the wedge between them is the story the chart tells.
 *
 * Returns empty series when there is nothing honest to draw (no goal, no
 * target date, or fewer than two weigh-ins).
 */
export function projectionSeries(
  logs: ReadonlyArray<WeightLog>,
  profile: UserProfileAI | null | undefined,
  now: Date,
  maxPoints = 8,
): ProjectionSeries {
  const targetKg = profile?.targetWeightKg ?? null;
  const targetIso = profile?.targetDate ?? null;
  const ordered = [...logs].sort(
    (a, b) => new Date(a.loggedAt).getTime() - new Date(b.loggedAt).getTime(),
  );
  if (ordered.length < 2 || targetKg == null || targetIso == null) {
    return { points: [], required: [], goalValue: targetKg ?? undefined };
  }

  const trimmed = ordered.slice(-maxPoints);
  const points: ProjectionPoint[] = trimmed.map((l) => ({ v: round1(l.weightKg) }));

  const startKg = profile?.currentWeightKg ?? trimmed[0]!.weightKg;
  const start = new Date(trimmed[0]!.loggedAt).getTime();
  const target = new Date(targetIso).getTime();
  const span = Math.max(1, target - start);
  // The required line runs the full window (start → target date), so it keeps
  // extending past the last weigh-in — that overhang is what shows the gap.
  const steps = Math.max(points.length, Math.min(maxPoints, 8));
  const elapsedShare = Math.min(1, Math.max(0, (now.getTime() - start) / span));
  const required: ProjectionPoint[] = Array.from({ length: steps }, (_, i) => {
    const t = i / Math.max(1, steps - 1);
    return { v: round1(startKg + (targetKg - startKg) * t) };
  });
  // Anchor the actual line against the same x scale: the points occupy the
  // share of the window that has actually elapsed.
  const lastX = Math.max(1, Math.round(elapsedShare * (steps - 1)));
  const spread = points.map((p, i) => ({
    ...p,
    i: points.length === 1 ? 0 : Math.round((i / (points.length - 1)) * lastX),
  }));

  return { points: spread, required, goalValue: round1(targetKg) };
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

/**
 * The review's receipts grid: one row per commitment across the seven days
 * the weekly review covers.
 */
export function reviewScorecardRows(
  perDay: WeeklyReviewPerDay[] | undefined,
): { label: string; marks: ScoreMark[] }[] {
  if (!perDay || perDay.length === 0) return [];
  const diet: ScoreMark[] = perDay.map((d) => {
    if (d.actualKcal === 0) return 'miss';
    if (d.dayMet) return 'hit';
    return d.actualKcal > d.targetKcal ? 'over' : 'under';
  });
  const protein: ScoreMark[] = perDay.map((d) => {
    if (d.actualProteinG === 0) return 'miss';
    return d.proteinWithinTolerance ? 'hit' : 'under';
  });
  return [
    { label: 'Diet', marks: diet },
    { label: 'Protein', marks: protein },
  ];
}

/** Weekday initials for the review's seven-day grid. */
export function reviewScorecardDays(
  perDay: WeeklyReviewPerDay[] | undefined,
  today: string,
): ScorecardDay[] {
  if (!perDay) return [];
  return perDay.map((d) => ({
    label: new Date(`${d.date}T00:00:00`).toLocaleDateString(undefined, { weekday: 'narrow' }),
    today: d.date === today,
  }));
}

// ── Home screen ──────────────────────────────────────────────────────────

export interface PlanItem {
  title: string;
  meta: string;
  done: boolean;
  to: string;
}

/**
 * Today's plan — three commitments derived from live data rather than a
 * stored checklist, so a row can never disagree with the numbers above it.
 *
 * Protein is the one macro the coach argues about, training is the day's
 * session, and the morning weigh-in is what feeds the projection. Each row
 * links to the screen that closes it.
 */
export function todaysPlan(input: {
  proteinG: number;
  proteinTargetG: number;
  trainedToday: boolean;
  weighedToday: boolean;
  latestWeightKg: number | null;
  weeklyGoal: number;
  sessionsThisWeek: number;
}): PlanItem[] {
  const remaining = Math.max(0, Math.round(input.proteinTargetG - input.proteinG));
  return [
    {
      title:
        input.proteinTargetG > 0
          ? `Hit ${Math.round(input.proteinTargetG)} g protein`
          : 'Log what you eat',
      meta:
        input.proteinTargetG > 0
          ? `${Math.round(input.proteinG)} g logged${remaining > 0 ? ` · ${remaining} to go` : ' · target met'}`
          : `${Math.round(input.proteinG)} g logged today`,
      done: input.proteinTargetG > 0 && input.proteinG >= input.proteinTargetG,
      to: '/log',
    },
    {
      title: input.trainedToday ? 'Session logged' : 'Train today',
      meta: `${input.sessionsThisWeek} of ${input.weeklyGoal} sessions this week`,
      done: input.trainedToday,
      to: '/workout/new',
    },
    {
      title: 'Log morning weight',
      meta:
        input.latestWeightKg != null
          ? `${input.latestWeightKg.toFixed(1)} kg last logged`
          : 'No weigh-in yet',
      done: input.weighedToday,
      to: '/profile',
    },
  ];
}
